import React, { useCallback, useEffect, useState, useRef } from "react";
import _ from "lodash";
import { convertHexToRGBA, filterSections } from "../../utils";
import { UNIT, SECTION_UPDATE_INTERVAL } from "../../constants";
import { getNewSections, updateMsgSectionRange } from "./ListProduct.services";
import { useAppSelector } from "../../redux/hook";
import ProductList from "./components/ProductList";
import {
  analyticsEvents,
  PAGES,
  PROVENANCE,
} from "../../utils/firebaseAnalytics";
import { SectionHeader } from "@dishopsaas/design-system";
import { Typography } from "@mui/material";

export default function ListProduct({ setSelectedSection, isMobile }) {
  const [messages, setMessages] = useState({});
  const [newSections, setNewSections] = useState([]);
  const { productOffers, bngmOffers } = useAppSelector(
    (state) => state.offerReducer
  );

  const { sections, categories: shopCategories } = useAppSelector(
    (state) => state.shopReducer
  );

  const { secondaryColor } = useAppSelector(
    (state) => state.configurationReducer,
  );
  const intervalRef = useRef(null);
  const observerRef = useRef(null);
  const lastActiveRef = useRef(null);

  const handleIntersect = useCallback(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const id = entry.target.id;
        const key = id.replace("section-", "");

        // avoid redundant state updates
        if (lastActiveRef.current !== key) {
          lastActiveRef.current = key;
          setSelectedSection(key);
        }

        // optional: stop after first visible match (more efficient)
        break;
      }
    },
    [setSelectedSection],
  );

  useEffect(() => {
    const elements = document.querySelectorAll(".target-section");

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: isMobile ? "-50px 0px -40% 0px" : "-80px 0px -80% 0px",
      threshold: 0,
    });

    observerRef.current = observer;

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [handleIntersect, isMobile, newSections]);

  useEffect(() => {
    analyticsEvents.logPageViewEvent(PAGES.PRODUCT, PROVENANCE.MENU_PAGE);
  }, []);

  useEffect(() => {
    setNewSections(
      getNewSections(
        productOffers,
        filterSections(sections),
        bngmOffers,
        shopCategories,
      ),
    );
  }, [sections, productOffers, bngmOffers, shopCategories]);

  useEffect(() => {
    const shouldUpdateSections = (sections: any) => {
      return _.some(sections, (section) => section?.activationRange);
    };

    const updateAndFilterSections = () => {
      if (shouldUpdateSections(sections)) {
        updateMsgSectionRange(newSections, messages, setMessages);
        setNewSections(getNewSections(productOffers, filterSections(sections), bngmOffers, shopCategories));
      }
    };
    updateAndFilterSections();

    if (shouldUpdateSections(sections)) {
      intervalRef.current = setInterval(
        updateAndFilterSections,
        SECTION_UPDATE_INTERVAL,
      );
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [sections, messages, productOffers, bngmOffers, shopCategories]);

  return (
    <div id="list-products" className="col" style={{ paddingBottom: "30vh" }}>
      {_.isEmpty(newSections) && (
        <div>
          <SectionHeader isLoading description />
          <ProductList isLoading section={null} messages={null} />
        </div>
      )}

      {_.map(newSections, (section) => (
        <div
          key={section.key}
          id={`section-${section.key}`}
          className="target-section"
        >
          <SectionHeader
            iconUrl={section.iconUrl}
            title={section.name}
            description={section.description}
          />
          {messages?.[section.key] && (
            <Typography
              variant="body1"
              style={{
                backgroundColor: convertHexToRGBA(secondaryColor, 0.2),
                textAlign: "center",
                color: secondaryColor,
                fontSize: 14,
                padding: UNIT * 0.8,
                marginBottom: 16,
              }}
            >
              {`Les produits de ${section.name
                } seront bientôt disponibles. La prochaine plage d'activation est ${messages[section.key]
                }.`}
            </Typography>
          )}
          <div style={{ marginBottom: "16px" }}>
            <ProductList section={section} messages={messages} />
          </div>
        </div>
      ))}
    </div>
  );
}
